import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const FilterPanel = ({ 
  filters, 
  onFilterChange, 
  searchHistory = [], 
  savedSearches = [],
  onLoadSavedSearch 
}) => {
  const [expandedSections, setExpandedSections] = useState({
    type: true,
    specialties: true,
    location: true,
    experience: false,
    other: false,
    history: false,
    saved: false
  });

  // Available filter options
  const partnerTypes = [
    { value: 'agent', label: 'Agent Musical', icon: 'Briefcase' },
    { value: 'venue', label: 'Salle/Venue', icon: 'MapPin' },
    { value: 'booker', label: 'Booker', icon: 'Calendar' },
    { value: 'independent', label: 'Indépendant', icon: 'User' }
  ];

  const artistProfessions = [
    { value: 'musician', label: 'Musicien', icon: 'Music' },
    { value: 'singer', label: 'Chanteur', icon: 'Mic' },
    { value: 'dj', label: 'DJ', icon: 'Disc3' },
    { value: 'producer', label: 'Producteur', icon: 'Headphones' },
    { value: 'model', label: 'Modèle', icon: 'Camera' },
    { value: 'actor', label: 'Acteur', icon: 'Film' },
    { value: 'magician', label: 'Magicien', icon: 'Sparkles' },
    { value: 'dancer', label: 'Danseur', icon: 'Users' },
    { value: 'performer', label: 'Performeur', icon: 'Star' },
    { value: 'other', label: 'Autre', icon: 'MoreHorizontal' }
  ];

  const musicalSpecialties = [
    { value: 'rock', label: 'Rock', icon: 'Zap' },
    { value: 'pop', label: 'Pop', icon: 'Heart' },
    { value: 'jazz', label: 'Jazz', icon: 'Piano' },
    { value: 'classical', label: 'Classique', icon: 'Music2' },
    { value: 'electronic', label: 'Électronique', icon: 'Zap' },
    { value: 'hip_hop', label: 'Hip-Hop', icon: 'Headphones' },
    { value: 'country', label: 'Country', icon: 'Guitar' },
    { value: 'blues', label: 'Blues', icon: 'Music3' },
    { value: 'reggae', label: 'Reggae', icon: 'Waves' },
    { value: 'folk', label: 'Folk', icon: 'TreePine' },
    { value: 'world', label: 'World', icon: 'Globe' },
    { value: 'latin', label: 'Latin', icon: 'Sun' },
    { value: 'metal', label: 'Metal', icon: 'Flame' },
    { value: 'punk', label: 'Punk', icon: 'Shield' },
    { value: 'indie', label: 'Indie', icon: 'Sparkles' },
    { value: 'alternative', label: 'Alternative', icon: 'Shuffle' }
  ];

  const experienceLevels = [
    { value: 'beginner', label: 'Débutant (< 2 ans)', icon: 'Seedling' },
    { value: 'intermediate', label: 'Intermédiaire (2-5 ans)', icon: 'TrendingUp' },
    { value: 'experienced', label: 'Expérimenté (5-10 ans)', icon: 'Award' },
    { value: 'expert', label: 'Expert (10+ ans)', icon: 'Crown' }
  ];

  const availabilityStatuses = [
    { value: 'available', label: 'Disponible', icon: 'CheckCircle', color: 'text-success' },
    { value: 'busy', label: 'Occupé', icon: 'Clock', color: 'text-warning' },
    { value: 'unavailable', label: 'Indisponible', icon: 'XCircle', color: 'text-error' }
  ];

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  // Handle filter change
  const handleArrayFilterChange = (filterKey, value, checked) => {
    const currentValues = filters?.[filterKey] || [];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues?.filter(v => v !== value);
    
    onFilterChange?.({
      ...filters,
      [filterKey]: newValues
    });
  };

  // Handle text filter change
  const handleTextFilterChange = (filterKey, value) => {
    onFilterChange?.({
      ...filters,
      [filterKey]: value
    });
  };

  // Handle boolean filter change
  const handleBooleanFilterChange = (filterKey, checked) => {
    onFilterChange?.({
      ...filters,
      [filterKey]: checked
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    onFilterChange?.({
      partner_type: [],
      musical_specialties: [],
      artist_profession: [],
      location: '',
      experience_level: [],
      availability_status: [],
      verified_only: false
    });
  };

  // Count active filters
  const activeFiltersCount = Object.values(filters || {})?.reduce((count, value) => {
    if (Array.isArray(value)) {
      return count + value?.length;
    } else if (typeof value === 'boolean') {
      return count + (value ? 1 : 0);
    } else if (typeof value === 'string') {
      return count + (value?.trim() ? 1 : 0);
    }
    return count;
  }, 0);

  // Render filter section
  const renderFilterSection = (title, key, children, defaultExpanded = true) => (
    <div className="border-b border-border pb-4 mb-4 last:border-b-0">
      <button
        onClick={() => toggleSection(key)}
        className="flex items-center justify-between w-full py-2 text-left hover:bg-accent/50 rounded-md px-2 -mx-2 transition-colors"
      >
        <h4 className="font-medium text-card-foreground">{title}</h4>
        <Icon 
          name={expandedSections?.[key] ? "ChevronDown" : "ChevronRight"} 
          size={16}
          className="text-muted-foreground"
        />
      </button>
      {expandedSections?.[key] && (
        <div className="mt-3 space-y-2">
          {children}
        </div>
      )}
    </div>
  );

  // Render checkbox group
  const renderCheckboxGroup = (options, filterKey, columns = 1) => (
    <div className={cn("grid gap-2", 
      columns === 2 ? "grid-cols-2" : "grid-cols-1"
    )}>
      {options?.map(option => (
        <label
          key={option?.value}
          className="flex items-center space-x-3 p-2 rounded-md hover:bg-accent/50 cursor-pointer transition-colors"
        >
          <Checkbox
            checked={filters?.[filterKey]?.includes(option?.value)}
            onChange={(checked) => handleArrayFilterChange(filterKey, option?.value, checked)}
          />
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <Icon 
              name={option?.icon} 
              size={16} 
              className={cn("text-muted-foreground flex-shrink-0", option?.color)}
            />
            <span className="text-sm font-medium text-card-foreground truncate">
              {option?.label}
            </span>
          </div>
        </label>
      ))}
    </div>
  );

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-sm sticky top-24">
      {/* Filter Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-card-foreground flex items-center space-x-2">
          <Icon name="Filter" size={20} />
          <span>Filtres</span>
          {activeFiltersCount > 0 && (
            <span className="bg-accent text-accent-foreground text-xs px-2 py-1 rounded-full">
              {activeFiltersCount}
            </span>
          )}
        </h3>
        {activeFiltersCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="text-xs"
          >
            Effacer tout
          </Button>
        )}
      </div>

      <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
        {/* Partner Type Filter */}
        {renderFilterSection("Type de partenaire", "type", 
          renderCheckboxGroup(partnerTypes, "partner_type")
        )}

        {/* Artist Profession Filter */}
        {renderFilterSection("Profession artistique", "profession", 
          renderCheckboxGroup(artistProfessions, "artist_profession", 2)
        )}

        {/* Musical Specialties Filter */}
        {renderFilterSection("Spécialités musicales", "specialties", 
          renderCheckboxGroup(musicalSpecialties, "musical_specialties", 2)
        )}

        {/* Location Filter */}
        {renderFilterSection("Localisation", "location",
          <Input
            placeholder="Ville, région, pays..."
            value={filters?.location || ''}
            onChange={(e) => handleTextFilterChange('location', e?.target?.value)}
            className="w-full"
          />
        )}

        {/* Experience Level Filter */}
        {renderFilterSection("Niveau d'expérience", "experience", 
          renderCheckboxGroup(experienceLevels, "experience_level")
        )}

        {/* Availability Status Filter */}
        {renderFilterSection("Statut de disponibilité", "availability", 
          renderCheckboxGroup(availabilityStatuses, "availability_status")
        )}

        {/* Other Filters */}
        {renderFilterSection("Autres options", "other",
          <div className="space-y-3">
            <label className="flex items-center space-x-3 p-2 rounded-md hover:bg-accent/50 cursor-pointer transition-colors">
              <Checkbox
                checked={filters?.verified_only || false}
                onChange={(checked) => handleBooleanFilterChange('verified_only', checked)}
              />
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-success" />
                <span className="text-sm font-medium text-card-foreground">
                  Profils vérifiés uniquement
                </span>
              </div>
            </label>
          </div>
        )}

        {/* Search History */}
        {searchHistory?.length > 0 && renderFilterSection("Recherches récentes", "history",
          <div className="space-y-2">
            {searchHistory?.slice(0, 5)?.map((search, index) => (
              <button
                key={`history-${search?.id || index}`}
                onClick={() => onLoadSavedSearch?.(search)}
                className="w-full text-left p-2 rounded-md hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Icon name="Clock" size={14} className="text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-card-foreground truncate">
                      {search?.query || 'Recherche personnalisée'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {search?.resultsCount} résultat{search?.resultsCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Saved Searches */}
        {savedSearches?.length > 0 && renderFilterSection("Recherches sauvegardées", "saved",
          <div className="space-y-2">
            {savedSearches?.slice(0, 5)?.map((search, index) => (
              <button
                key={`saved-${search?.id || index}`}
                onClick={() => onLoadSavedSearch?.(search)}
                className="w-full text-left p-2 rounded-md hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Icon name="Bookmark" size={14} className="text-accent" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-card-foreground truncate">
                      {search?.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {search?.results_count} résultat{search?.results_count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterPanel;